import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Wifi, Coffee, Users, Check, ArrowRight, Layout, Music, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardContent, CardHeader, CardFooter } from '../components/Card';
import { Tabs, TabList, Tab, TabPanel } from '../components/Tabs';
const gallerySlides = [[{
  label: 'Koffiebar ruimte',
  height: 'h-64'
}, {
  label: 'Vergaderopstelling',
  height: 'h-40'
}, {
  label: 'Bar detail',
  height: 'h-48'
}], [{
  label: 'Atelier ruimte',
  height: 'h-48'
}, {
  label: 'Workshop opstelling',
  height: 'h-64'
}, {
  label: 'Materiaal hoek',
  height: 'h-40'
}], [{
  label: 'Terras',
  height: 'h-40'
}, {
  label: 'Ontvangstruimte',
  height: 'h-56'
}]];
export function LocationRental() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % gallerySlides.length);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + gallerySlides.length) % gallerySlides.length);
  }, []);
  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);
  const highlights = [{
    icon: <MapPin size={24} data-id="element-791" />,
    title: 'Centraal gelegen',
    description: 'Makkelijk bereikbaar met openbaar vervoer en auto.'
  }, {
    icon: <Layout size={24} data-id="element-792" />,
    title: 'Flexibele opstelling',
    description: 'Ruimtes aanpasbaar aan jouw noden en groepsgrootte.'
  }, {
    icon: <Coffee size={24} data-id="element-793" />,
    title: 'Catering mogelijk',
    description: 'Van koffiepauze tot uitgebreide lunch of receptie.'
  }, {
    icon: <Users size={24} data-id="element-794" />,
    title: 'Eigen barista',
    description: 'Professionele bediening voor de perfecte koffie.'
  }, {
    icon: <Music size={24} data-id="element-795" />,
    title: 'Creatieve sfeer',
    description: 'Inspirerende omgeving die creativiteit stimuleert.'
  }, {
    icon: <Wifi size={24} data-id="element-796" />,
    title: 'Gratis WiFi & AV',
    description: 'Snel internet en presentatiemateriaal inbegrepen.'
  }];
  const formulas = [{
    title: 'Vergadering met koffie',
    price: '€150/halve dag',
    description: 'Ideaal voor team meetings en brainstormsessies.',
    features: ['Vergaderruimte', 'Koffie, thee & water', 'Max 12 personen']
  }, {
    title: 'Workshop op maat',
    price: '€250/dag',
    description: 'Volledig uitgerust atelier voor creatieve sessies.',
    features: ['Atelier gebruik', 'Basismateriaal', 'Koffie inbegrepen', 'Max 20 personen']
  }, {
    title: 'Feest of receptie',
    price: 'Op aanvraag',
    description: 'Unieke locatie voor jouw speciale gelegenheid.',
    features: ['Koffiebar exclusief', 'Catering opties', 'Decoratie mogelijk', 'Max 30 personen']
  }, {
    title: 'Coworking dag',
    price: '€25/persoon',
    description: 'Rustige werkplek in een creatieve omgeving.',
    features: ['Flexibele werkplek', 'Snelle WiFi', 'Onbeperkt koffie', 'Flexibel aantal']
  }];
  const renderCalendar = (space: 'koffiebar' | 'atelier') => {
    const days = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
    const timeSlots = ['09:00', '11:00', '13:00', '15:00', '17:00', '19:00'];
    return <div className="overflow-x-auto" data-id="element-797">
        <div className="min-w-[600px]" data-id="element-798">
          <div className="grid grid-cols-8 gap-2 mb-2" data-id="element-799">
            <div className="font-bold text-gray-400 text-sm" data-id="element-800">Tijd</div>
            {days.map(day => <div key={day} className="font-bold text-center text-sm" data-id="element-801">
                {day}
              </div>)}
          </div>
          {timeSlots.map((time, timeIndex) => <div key={time} className="grid grid-cols-8 gap-2 mb-2" data-id="element-802">
              <div className="text-sm text-gray-500 flex items-center" data-id="element-803">
                {time}
              </div>
              {days.map((day, dayIndex) => {
            const isOccupied = space === 'koffiebar' ? (dayIndex + timeIndex) % 5 === 0 || dayIndex === 5 && timeIndex > 2 : dayIndex * timeIndex % 4 === 0 || dayIndex === 2 && timeIndex < 3;
            return <div key={`${day}-${time}`} className={`h-12 rounded border ${isOccupied ? 'bg-gray-200 border-gray-300 flex items-center justify-center' : 'bg-white border-gray-200'}`} data-id="element-804">
                    {isOccupied && <span className="text-xs text-gray-500 font-medium" data-id="element-805">
                        Bezet
                      </span>}
                  </div>;
          })}
            </div>)}
        </div>
      </div>;
  };
  return <div className="container mx-auto px-4 py-10" data-id="element-806">
      {/* Hero Section */}
      <section className="text-center mb-16 max-w-4xl mx-auto" data-id="element-807">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-808">Locatie huren</h1>
        <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-809">
          De Maekerij biedt twee unieke ruimtes te huur aan voor jouw evenement,
          vergadering of workshop. Kies voor de gezellige sfeer van onze{' '}
          <span className="font-bold" data-id="element-810">Koffiebar</span> (tot 30 personen) of de
          creatieve omgeving van ons <span className="font-bold" data-id="element-811">Atelier</span>{' '}
          (tot 20 personen).
        </p>
      </section>

      {/* Highlights (Troeven) */}
      <section className="mb-8" data-id="element-812">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center" data-id="element-813">
          Onze troeven
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-814">
          {highlights.map((item, index) => <Card key={index} variant="outlined" data-id="element-815">
              <CardContent className="flex flex-col items-center text-center p-6" data-id="element-816">
                <div className="mb-4 p-3 bg-gray-100 rounded-full text-gray-700" data-id="element-817">
                  {item.icon}
                </div>
                <h3 className="font-bold text-lg mb-2" data-id="element-818">{item.title}</h3>
                <p className="text-gray-600 text-sm" data-id="element-819">{item.description}</p>
              </CardContent>
            </Card>)}
        </div>
      </section>

      {/* CTA after troeven */}
      <div className="text-center mb-16" data-id="element-820">
        <Link to="/locatie-huren/aanvraag" data-id="element-821">
          <Button variant="primary" size="large" rightIcon={<ArrowRight size={18} data-id="element-823" />} data-id="element-822">
            Maak een reservatie
          </Button>
        </Link>
      </div>

      {/* Photo Gallery Slider (masonry-style) */}
      <section className="mb-16 relative" data-id="element-824">
        <div className="relative overflow-hidden" data-id="element-825">
          {gallerySlides.map((slide, slideIndex) => <div key={slideIndex} className={`transition-opacity duration-500 ${slideIndex === currentSlide ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`} data-id="element-826">
              <div className={`grid gap-4 ${slide.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`} data-id="element-827">
                {slide.map((photo, photoIndex) => <div key={photoIndex} className={`border-2 border-dashed border-gray-300 ${photo.height} flex items-center justify-center bg-gray-50 rounded-lg ${slide.length === 3 && photoIndex === 0 ? 'md:row-span-2 md:h-full' : ''}`} data-id="element-828">
                    <span className="text-gray-400 font-medium" data-id="element-829">
                      {photo.label}
                    </span>
                  </div>)}
              </div>
            </div>)}

          {/* Prev / Next controls */}
          <button onClick={prevSlide} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center border border-gray-300 bg-white rounded-full hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 z-10" aria-label="Vorige foto's" data-id="element-830">
            <ChevronLeft size={20} data-id="element-831" />
          </button>
          <button onClick={nextSlide} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center border border-gray-300 bg-white rounded-full hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 z-10" aria-label="Volgende foto's" data-id="element-832">
            <ChevronRight size={20} data-id="element-833" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-4" data-id="element-834">
          {gallerySlides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2.5 h-2.5 rounded-full border border-gray-400 transition-colors duration-150 focus:outline-none ${index === currentSlide ? 'bg-gray-700' : 'bg-white'}`} aria-label={`Ga naar slide ${index + 1}`} data-id="element-835" />)}
        </div>
      </section>

      {/* Availability Calendar */}
      <section className="mb-8" data-id="element-836">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center" data-id="element-837">
          Beschikbaarheid
        </h2>
        <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-838">
          Bekijk hieronder de beschikbaarheid van onze ruimtes. Dit is een
          indicatie, neem contact op voor de definitieve planning.
        </p>

        <div className="max-w-4xl mx-auto" data-id="element-839">
          <Tabs defaultTab="koffiebar" variant="underlined" data-id="element-840">
            <TabList className="mb-6 justify-center" data-id="element-841">
              <Tab id="koffiebar" icon={<Coffee size={18} data-id="element-843" />} data-id="element-842">
                Koffiebar
              </Tab>
              <Tab id="atelier" icon={<Layout size={18} data-id="element-845" />} data-id="element-844">
                Atelier
              </Tab>
            </TabList>

            <TabPanel id="koffiebar" data-id="element-846">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-847">
                <h3 className="font-bold mb-4 text-lg" data-id="element-848">
                  Beschikbaarheid Koffiebar
                </h3>
                {renderCalendar('koffiebar')}
              </div>
            </TabPanel>

            <TabPanel id="atelier" data-id="element-849">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-id="element-850">
                <h3 className="font-bold mb-4 text-lg" data-id="element-851">
                  Beschikbaarheid Atelier
                </h3>
                {renderCalendar('atelier')}
              </div>
            </TabPanel>
          </Tabs>
          <p className="text-center text-sm text-gray-500 mt-4 italic" data-id="element-852">
            * Beschikbaarheid is indicatief. Neem contact op voor exacte
            beschikbaarheid.
          </p>
        </div>
      </section>

      {/* CTA after calendar */}
      <div className="text-center mb-16" data-id="element-853">
        <Link to="/locatie-huren/aanvraag" data-id="element-854">
          <Button variant="primary" size="large" rightIcon={<ArrowRight size={18} data-id="element-856" />} data-id="element-855">
            Maak een reservatie
          </Button>
        </Link>
      </div>

      {/* Formulas */}
      <section className="mb-16 bg-gray-50 -mx-4 px-4 py-16 md:mx-0 md:rounded-xl" data-id="element-857">
        <div className="max-w-6xl mx-auto" data-id="element-858">
          <div className="text-center mb-12" data-id="element-859">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-860">
              Onze formules
            </h2>
            <p className="text-gray-600" data-id="element-861">
              Kies uit een van onze vaste formules of stel je eigen programma
              samen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-862">
            {formulas.map((formula, index) => <Card key={index} variant="elevated" className="h-full flex flex-col" data-id="element-863">
                <CardHeader title={formula.title} className="pb-2" data-id="element-864" />
                <CardContent className="flex-grow" data-id="element-865">
                  <div className="text-2xl font-bold text-gray-900 mb-4" data-id="element-866">
                    {formula.price}
                  </div>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-867">
                    {formula.description}
                  </p>
                  <ul className="space-y-2" data-id="element-868">
                    {formula.features.map((feature, i) => <li key={i} className="flex items-start text-sm text-gray-700" data-id="element-869">
                        <Check size={16} className="mr-2 mt-0.5 text-green-600 flex-shrink-0" data-id="element-870" />
                        <span data-id="element-871">{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter data-id="element-872">
                  <Link to="/locatie-huren/aanvraag" className="w-full" data-id="element-873">
                    <Button variant="secondary" size="small" className="w-full" data-id="element-874">
                      Kies formule
                    </Button>
                  </Link>
                </CardFooter>
              </Card>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="text-center max-w-3xl mx-auto mb-12" data-id="element-875">
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 md:p-12" data-id="element-876">
          <h2 className="text-2xl font-bold mb-4" data-id="element-877">Klaar om te reserveren?</h2>
          <p className="text-gray-600 mb-8" data-id="element-878">
            Vul ons aanvraagformulier in en we bekijken samen de mogelijkheden.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-879">
            <Link to="/locatie-huren/aanvraag" data-id="element-880">
              <Button variant="primary" size="large" rightIcon={<ArrowRight size={18} data-id="element-882" />} data-id="element-881">
                Reservatie aanvragen
              </Button>
            </Link>
            <Link to="/contact" data-id="element-883">
              <Button variant="secondary" size="large" data-id="element-884">
                Heb je een ander idee?
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}